import { useMemo, useRef } from 'react';

import {
    useIslandFallback,
    useScrolledPast,
    type IslandMode,
} from '~/ui-rocco/pages/detail-shared';
import { ISLAND_ENQUIRY_CLAIM_FRACTION } from '../../hooks/use-enquiry-owns-island';

interface DesktopIslandEnquiryFallbackProps {
    /**
     * The price as the page states it, prefix and all — "From $4,000 - $16,000".
     * Null hides it. The island is the page's own claim once the CTA cluster
     * has scrolled away, and a range quoted without its "From" reads as a
     * fixed price.
     */
    priceString: string | null;
    /** Enquiry action label (e.g. "Enquire with Nero Tapware"). */
    enquiryLabel: string;
    /** The professional's enquiry CTA may show (audience gating). */
    show: boolean;
    /** Opens the enquiry modal with the current selection folded in. */
    onEnquire: () => void;
}

/**
 * Null-rendering owner of the desktop island's enquiry fallback — the pill
 * shown whenever no scrolled section claims the island, once the reader is
 * 30% into the page (the project page's Book-a-Meeting timing). The section
 * claims (About-the-Supplier carousel, Similar Products pagination) keep
 * priority; the fallback fills the gaps between them so the primary QDD
 * action stays one click away through the depth. The mobile flow registers
 * its own persistent pill — this is desktop's.
 */
export const DesktopIslandEnquiryFallback = ({
    priceString,
    enquiryLabel,
    show,
    onEnquire,
}: DesktopIslandEnquiryFallbackProps) => {
    const scrolledIntoDepth = useScrolledPast(ISLAND_ENQUIRY_CLAIM_FRACTION);

    // Route the action through a ref so the memoised mode's identity only
    // tracks meaningful state — an unstable mode would re-run setFallback →
    // context change → re-render, in a loop.
    const onEnquireRef = useRef(onEnquire);
    onEnquireRef.current = onEnquire;

    const mode = useMemo<IslandMode | null>(
        () =>
            show
                ? {
                      kind: 'purchase',
                      price: priceString,
                      label: enquiryLabel,
                      onClick: () => onEnquireRef.current(),
                  }
                : null,
        [show, priceString, enquiryLabel],
    );
    useIslandFallback(scrolledIntoDepth ? mode : null);

    return null;
};
